/**
 * <RefinementRun> screen — detail view for a single refinement run (FR-007, FR-008).
 *
 * Mounted at `#/refine/:id`. Displays:
 * - Metadata strip with `data-run-status={run.statusKey}`
 * - Passes timeline (one row per pass, passIndex ascending)
 * - Recharts convergence chart (contentDelta + structuralDelta, adaptive Y-axis)
 * - Per-pass artifact list with target="_blank" rel="noopener noreferrer"
 * - Cancel button (absent when run.isTerminal) with ConfirmByTyping modal
 *
 * All toast dispatches route through emitToast (FR-013).
 * Numeric deltas/thresholds displayed with toFixed(3).
 * Refinements breadcrumb is a non-clickable <span>.
 */
import '../../styles/screens/refine.css';
import React, { useState, useCallback, type ReactNode, Component } from "react";
import { TypingDots } from "../../components/realtime/TypingDots";
import { useParams } from "../../lib/router";
import { useMutation } from "convex/react";
import { ConvexError } from "convex/values";
import * as Sentry from "@sentry/react";
import { api } from "../../../../../convex/_generated/api";
import ConvergenceChartViz from "../../components/visualisations/ConvergenceChart";
import type { ConvergencePass } from "../../components/visualisations/types";
import type { Id } from "../../../../../convex/_generated/dataModel";
import { useRefinementRun } from "../../api/hooks/useRefinementRun";
import { emitToast } from "../../api/utils/toast";
import { useT } from "../../i18n/useT";
import EvalsStrip from "../../components/runs/EvalsStrip";
import { useAgentEvals } from "../../api/hooks/useAgentEvals";
import type {
  RefinementPassRow,
  ConvergencePoint,
  ArtifactEntry,
} from "../../api/types/refinementRun";

// ---------------------------------------------------------------------------
// Inline primitives — avoids importing from primitives/index.ts (.ts with JSX)
// These match the interface contract used by the test harness.
// ---------------------------------------------------------------------------

function SkeletonRow() {
  const t = useT();

  return <div style={{ height: "48px", backgroundColor: "var(--color-skeleton-bg, rgba(0,0,0,0.04))", borderRadius: "8px", marginBottom: "8px" }} aria-hidden="true" />;
}

function EmptyState({ children }: { children: ReactNode }) {
  return <div role="status" style={{ textAlign: "center", padding: "32px 16px" }}>{children}</div>;
}

function Button({ children, onClick, disabled, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" onClick={onClick} disabled={disabled} {...rest}>{children}</button>;
}

function CCModal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "var(--color-overlay-bg, rgba(26,22,20,0.4))", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title} style={{ backgroundColor: "var(--color-surface-primary, #fff)", borderRadius: "12px", padding: "24px", maxWidth: "90vw", minWidth: "320px" }} onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error boundary — catches ConvexError FORBIDDEN and renders forbidden state
// ---------------------------------------------------------------------------

interface DetailErrorBoundaryState {
  errorKind: "forbidden" | "error" | null;
}

class DetailErrorBoundary extends Component<
  { children: ReactNode },
  DetailErrorBoundaryState
> {
  state: DetailErrorBoundaryState = { errorKind: null };

  static getDerivedStateFromError(error: unknown): DetailErrorBoundaryState {
    if (error instanceof ConvexError) {
      const code = (error.data as { code?: string })?.code;
      if (code === "UNAUTHORIZED" || code === "FORBIDDEN") {
        return { errorKind: "forbidden" };
      }
      return { errorKind: "error" };
    }
    return { errorKind: "error" };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    if (!(error instanceof ConvexError)) {
      try {
        Sentry.captureException(error, {
          extra: { componentStack: info.componentStack },
          tags: { component: "DetailErrorBoundary" },
        });
      } catch {
        // Sentry capture must never crash the error boundary.
      }
    }
  }

  render(): ReactNode {
    if (this.state.errorKind === "forbidden") {
      return (
        <EmptyState>
          <p>You do not have permission to view this refinement run.</p>
        </EmptyState>
      );
    }
    if (this.state.errorKind === "error") {
      return (
        <EmptyState>
          <p>Something went wrong loading this run.</p>
        </EmptyState>
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Cancel confirmation modal (ConfirmByTyping pattern)
// ---------------------------------------------------------------------------

function CancelModal({
  onSubmit,
  onClose,
}: {
  onSubmit: (reason: string) => void;
  onClose: () => void;
}) {
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");

  const isConfirmed = confirmText === "CANCEL";

  const handleSubmit = () => {
    if (isConfirmed) {
      onSubmit(reason);
    }
  };

  return (
    <CCModal title="Cancel this run?" onClose={onClose}>
      <p>
        This action cannot be undone. Type <strong>CANCEL</strong> to confirm.
      </p>

      <div>
        <label htmlFor="cancel-confirm-input">
          Type CANCEL to confirm
        </label>
        <input
          id="cancel-confirm-input"
          type="text"
          role="textbox"
          aria-label="Type CANCEL to confirm"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="CANCEL"
          autoComplete="off"
        />
      </div>

      <div>
        <label htmlFor="cancel-reason-textarea">
          Reason (optional)
        </label>
        <textarea
          id="cancel-reason-textarea"
          role="textbox"
          aria-label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={4096}
          placeholder="Why are you cancelling this run?"
        />
        <p>{reason.length} / 4096</p>
      </div>

      <div>
        <Button onClick={onClose}>
          Dismiss
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!isConfirmed}
        >
          Submit
        </Button>
      </div>
    </CCModal>
  );
}

// ---------------------------------------------------------------------------
// Passes timeline
// ---------------------------------------------------------------------------

function PassesTimeline({
  passes,
}: {
  passes: RefinementPassRow[];
}) {
  const t = useT();

  if (passes.length === 0) {
    return (
      <div>
        <p>Waiting for the first pass to start. This usually takes 5-15 seconds.</p>
      </div>
    );
  }

  const sorted = [...passes].sort((a, b) => a.passNumber - b.passNumber);

  return (
    <div>
      <h3>Passes</h3>
      <ul role="list">
        {sorted.map((pass) => (
          <li key={pass.id}>
            <span>Pass #{pass.passNumber}</span>
            {pass.statusLabel && <span> — {pass.statusLabel}</span>}
            {pass.contentDelta !== undefined && (
              <span> | Content delta: {pass.contentDelta.toFixed(3)}</span>
            )}
            {pass.structuralDelta !== undefined && (
              <span> | Structural delta: {pass.structuralDelta.toFixed(3)}</span>
            )}
            {pass.belowConvergenceThreshold !== undefined && (
              <span>
                {pass.belowConvergenceThreshold
                  ? t("run.converged")
                  : " (not converged)"}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Convergence chart — uses the shared ConvergenceChart visualisation primitive
// ---------------------------------------------------------------------------

function ConvergenceSection({
  convergence,
  contentThreshold,
  structuralThreshold,
}: {
  convergence: ConvergencePoint[];
  contentThreshold: number;
  structuralThreshold: number;
}) {
  if (convergence.length === 0) {
    return null;
  }

  const sorted = [...convergence].sort((a, b) => a.passIndex - b.passIndex);

  // Map domain-specific ConvergencePoint to the shared ConvergencePass shape.
  // score = 1 - contentDelta (lower delta = higher convergence score).
  // oscillationFlag = contentDelta increased vs prior pass.
  const passes: ConvergencePass[] = sorted.map((c, i) => ({
    passNumber: c.passIndex,
    score: Math.max(0, Math.min(1, 1 - c.contentDelta)),
    oscillationFlag:
      i > 0 && sorted[i - 1] !== undefined
        ? c.contentDelta > sorted[i - 1]!.contentDelta
        : false,
  }));

  return (
    <div>
      <h3>Convergence</h3>
      <ConvergenceChartViz
        data={passes}
        thresholdConverged={Math.max(0, Math.min(1, 1 - contentThreshold))}
        thresholdMinimumViable={Math.max(0, Math.min(1, 1 - structuralThreshold))}
        ariaLabel="Refinement convergence by pass"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Artifact list
// ---------------------------------------------------------------------------

function ArtifactList({
  artifacts,
}: {
  artifacts: ArtifactEntry[];
}) {
  if (artifacts.length === 0) {
    return null;
  }

  const sorted = [...artifacts].sort((a, b) => a.passIndex - b.passIndex);

  return (
    <div>
      <h3>Artifacts</h3>
      <ul role="list">
        {sorted.map((artifact) => (
          <li key={artifact.id}>
            <a
              href={artifact.uri}
              target="_blank"
              rel="noopener noreferrer"
            >
              {artifact.task} (pass {artifact.passIndex})
            </a>
            {artifact.sha && (
              <span> — SHA: {artifact.sha.slice(0, 12)}</span>
            )}
            {artifact.bytes > 0 && (
              <span> — {(artifact.bytes / 1024).toFixed(1)} KB</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail body — rendered inside the error boundary
// ---------------------------------------------------------------------------

function RefinementRunBody() {
  const { id } = useParams<{ id: string }>();
  const runId = id as Id<"workflowRuns">;
  const t = useT();

  const result = useRefinementRun(runId);
  const evalsResult = useAgentEvals();
  const cancelRunMutation = useMutation(api.workflowRuns.cancelRun);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const handleCancelSubmit = useCallback(
    async (reason: string) => {
      try {
        await cancelRunMutation({ runId, reason });
        emitToast(t("run.cancelled"), "success");
        setCancelModalOpen(false);
      } catch (err) {
        if (err instanceof ConvexError) {
          const code = (err.data as { code?: string })?.code;
          if (code === "VALIDATION") {
            emitToast(t("run.reason-too-long"), "error");
          } else {
            emitToast(t("run.cancel-failed"), "error");
          }
        } else {
          emitToast(t("run.cancel-failed"), "error");
        }
      }
    },
    [runId, cancelRunMutation, t],
  );

  // ── Loading state ────────────────────────────────────────────────────
  if (result.status === "loading") {
    return (
      <div aria-busy="true">
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );
  }

  // ── Not found state ──────────────────────────────────────────────────
  if (result.status === "not_found") {
    return (
      <EmptyState>
        <p>This refinement run does not exist or has been removed.</p>
      </EmptyState>
    );
  }

  // ── Forbidden state (structural completeness) ────────────────────────
  if (result.status === "forbidden") {
    return (
      <EmptyState>
        <p>You do not have permission to view this refinement run.</p>
      </EmptyState>
    );
  }

  // ── Ready state ──────────────────────────────────────────────────────
  const { run, passes, convergence, artifacts } = result.data;

  return (
    <div className="refine__root">
      {/* Stacked header — mirrors the design's screens-refine.jsx structure */}
      <header className="refine__header">
        <nav aria-label="Breadcrumb" className="refine__breadcrumb">
          <span>Refinements</span>
          <span aria-hidden="true"> / </span>
          <span className="refine__breadcrumb-current">{run.id}</span>
        </nav>
        <div className="refine__header-row">
          <div className="refine__header-text">
            <div className="refine__header-meta">
              <span className="refine__run-id-mono">{run.id}</span>
              <span
                className="refine__status-chip"
                style={{
                  color: run.statusColor,
                  backgroundColor: run.statusBgColor,
                }}
              >
                {run.statusLabel}
              </span>
              <span className="refine__pass-counter">
                PASS {passes.length} OF {run.maxReviewIterations}
              </span>
            </div>
            <h1 className="refine__heading">Refinement run</h1>
            <p className="refine__subhead">
              Capicúa is iterating on this brief, looking for terminology drift,
              missing rationale, structural problems, and edge cases. We&apos;ll
              stop when the brief converges.
            </p>
          </div>
          <div className="refine__header-actions">
            {!run.isTerminal && (
              <Button onClick={() => setCancelModalOpen(true)}>
                Cancel run
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Failure banner — surfaces workflowRuns.errorMessage so a failed run
          shows *why* it failed instead of just a "Failed" chip. */}
      {run.errorMessage && (
        <section
          className="refine__card refine__error-banner"
          role="alert"
          aria-label="Run failure reason"
        >
          <h2 className="refine__section-heading">This run failed</h2>
          <p className="refine__error-detail">{run.errorMessage}</p>
        </section>
      )}

      {/* Metadata card — KV strip */}
      <section
        className="refine__card refine__meta-card"
        data-run-status={run.statusKey}
        aria-label="Run details"
      >
        <dl className="refine__kv">
          <div className="refine__kv-pair">
            <dt>Kind</dt>
            <dd>{run.kindLabel}</dd>
          </div>
          <div className="refine__kv-pair">
            <dt>Pass</dt>
            <dd>{passes.length} / {run.maxReviewIterations}</dd>
          </div>
          <div className="refine__kv-pair">
            <dt>Content threshold</dt>
            <dd>{run.contentThreshold.toFixed(3)}</dd>
          </div>
          <div className="refine__kv-pair">
            <dt>Structural threshold</dt>
            <dd>{run.structuralThreshold.toFixed(3)}</dd>
          </div>
        </dl>
      </section>

      {/* Active-run streaming indicator */}
      {!run.isTerminal && (
        <div className="refine__live-indicator" aria-live="polite">
          <TypingDots aria-label="Refinement in progress" />
          <span>Refinement in progress…</span>
        </div>
      )}

      {/* Passes timeline */}
      <section className="refine__card refine__section" aria-label="Passes">
        <h2 className="refine__section-heading">Passes</h2>
        <PassesTimeline passes={passes} />
      </section>

      {/* Agent evaluation metrics (FR-009) */}
      {evalsResult.status === "ready" && evalsResult.data.latest !== null && (
        <section className="refine__card refine__section" aria-label="Evaluations">
          <h2 className="refine__section-heading">Agent evaluations</h2>
          <EvalsStrip rollup={evalsResult.data.latest} />
        </section>
      )}

      {/* Convergence chart */}
      <section className="refine__card refine__chart-section" aria-label="Convergence">
        <h2 className="refine__section-heading">Convergence chart</h2>
        <p className="refine__section-caption">
          Each point is one pass. The line should drop and cross both thresholds
          before we call the brief done.
        </p>
        <ConvergenceSection
          convergence={convergence}
          contentThreshold={run.contentThreshold}
          structuralThreshold={run.structuralThreshold}
        />
      </section>

      {/* Artifacts */}
      <section className="refine__card refine__section" aria-label="Artifacts">
        <h2 className="refine__section-heading">Artifacts</h2>
        <ArtifactList artifacts={artifacts} />
      </section>

      {/* Cancel modal */}
      {cancelModalOpen && (
        <CancelModal
          onSubmit={handleCancelSubmit}
          onClose={() => setCancelModalOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-level exported component
// ---------------------------------------------------------------------------

export function RefinementRun(): React.JSX.Element {
  return (
    <DetailErrorBoundary>
      <RefinementRunBody />
    </DetailErrorBoundary>
  );
}
